import { Timestamp } from '@lemon/core/model/common'

/** Lähdejärjestelmä, josta tiedosto on peräisin. Laajennetaan kun uusia formaatteja tuetaan. */
export type ToimittajaImportLahdejarjestelma = 'netvisor' | 'procountor'

/** Virhekoodit toimittaja-import-operaatioista. Frontend tulkkaa nämä käyttäjälle näytettäviksi teksteiksi. */
export enum ToimittajaImportVirhekoodi {
  METADATA_EI_LOYDY = 'metadata-ei-loydy',
  TIEDOSTOA_EI_LOYDY = 'tiedostoa-ei-loydy',
  OTSIKKORIVI_PUUTTUU = 'otsikkorivi-puuttuu',
  SARAKKEET_PUUTTUVAT = 'sarakkeet-puuttuvat',
  EI_TOIMITTAJIA = 'ei-toimittajia',
  PARSINTAVIRHE = 'parsintavirhe',
  LAHDEJARJESTELMA_EPAVARMA = 'lahdejarjestelma-epavarma',
  TUNTEMATON_VIRHE = 'tuntematon-virhe',
}

/** Yksi pankkitili tuontirivillä. tyyppi = 'oletus' vain ensimmäisellä rivillä — loput ovat 'tavallinen'. */
export interface ToimittajaImportIban {
  iban: string
  bic: string
  tyyppi: 'oletus' | 'tavallinen'
}

/** Sisäinen tietomalli: yksi lähdetiedostosta parsittu toimittajarivi (ennen tallennusta). */
export interface ToimittajaImportRivi {
  /** Alkuperäinen rivinumero lähdetiedostossa (1-pohjainen, otsikkorivi ei lasketa) */
  rivinumero: number
  /** Toimittajakoodi lähdejärjestelmässä (Netvisorilla "Koodi"). Tyhjä jos lähde ei tarjoa koodia. */
  ulkoinenTunniste: string
  nimi: string
  ytunnus: string
  /** Pankkitilit oletuksena tyyppi='oletus' ensimmäisellä, loput 'tavallinen'. Tyhjä lista jos ei tilejä. */
  ibanit: ToimittajaImportIban[]
}

/** Pyyntö tiedoston parsimiseksi. Tiedosto on jo arkistossa (uploadattu hallintokansiossa). */
export interface ToimittajaImportParsePyynto {
  asiakasAvain: string
  /** Arkistotiedoston metadata-avain (ArkistoFile.avain) */
  arkistoTiedostonAvain: string
  /** Jos puuttuu, backend tunnistaa lähdejärjestelmän tiedoston sisällöstä. */
  lahdejarjestelma?: ToimittajaImportLahdejarjestelma
}

/** Vastaus parsinnasta. Sisältää esikatselurivit, ei tallenna mitään. */
export interface ToimittajaImportParseVastaus {
  onnistui: boolean
  virhekoodi?: ToimittajaImportVirhekoodi
  virheviesti?: string
  rivit?: ToimittajaImportRivi[]
  /** Parsinnassa käytetty lähdejärjestelmä (pyynnössä annettu tai tiedostosta tunnistettu) */
  lahdejarjestelma?: ToimittajaImportLahdejarjestelma
}

/** Pyyntö esikatseltujen toimittajien tallentamiseksi asiakkaan toimittajarekisteriin. */
export interface ToimittajaImportTallennaPyynto {
  asiakasAvain: string
  rivit: ToimittajaImportRivi[]
  arkistoTiedostonAvain: string
  lahdejarjestelma: ToimittajaImportLahdejarjestelma
}

/** Vastaus tallennuksesta. */
export interface ToimittajaImportTallennaVastaus {
  onnistui: boolean
  virhekoodi?: ToimittajaImportVirhekoodi
  tallennetut?: number
  ohitetutDuplikaatit?: number
}

/**
 * Asiakkaan tallennettu toimittaja-import-dokumentti — viimeisimmän tuonnin metadata.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/toimittaja-import
 */
export interface ToimittajaImportDocument {
  tuotu: Timestamp
  lahdejarjestelma: ToimittajaImportLahdejarjestelma
  tuoja: string
  arkistoTiedostonAvain: string
  tiedostonNimi: string
  tallennetutToimittajat: number
  ohitetutDuplikaatit?: number
}

/**
 * Yksittäisen tuonnin historiamerkintä — yksi rivi per käyttäjän käynnistämä tallennus.
 * Firestore-polku: /asiakkaat/{asiakasAvain}/muut/toimittaja-import/toimittaja-import-historia/{historiaAvain}
 */
export interface ToimittajaImportHistoriaDocument extends ToimittajaImportDocument {
  avain: string
}
